import { Edit } from "@mui/icons-material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import {
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
} from "@mui/material";
import noImage from "assets/images/no-image.png";
import Button from "components/common/Button";
import Input from "components/common/Input";
import api from "config/api";
import { format } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react"; // === ADDED BY SAMPATH – 2026-01-12 ===
import { toast } from "react-toastify";
import { ProductType } from "utils/types";

type Props = {
  selected?: ProductType;
  setSelected: (v?: any) => void;
  onComplete: (v?: boolean) => void;
};

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const days = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

const EditProductDialog = ({ setSelected, selected, onComplete }: Props) => {
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState<File | undefined>(undefined);
  const [data, setData] = useState<ProductType | undefined>(undefined);
  const [disableValue, setDisableValue] = useState("none");
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [toppingPriceRows, setToppingPriceRows] = useState<any[]>([]);
  const [pizzaSizeRows, setPizzaSizeRows] = useState<any[]>([]);

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // FIX: prevent showing old cached image after delete/upload by busting URL cache + resetting file input
  const [imageCacheBuster, setImageCacheBuster] = useState<number>(Date.now());
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  // === END SAMPATH ===

  const handleCheckboxChange = (dayValue: number) => {
    setSelectedDays((prev) => {
      const updated = prev.includes(dayValue)
        ? prev.filter((v) => v !== dayValue)
        : [...prev, dayValue];
      return updated;
    });
  };

  useEffect(() => {
    if (selected) {
      setData(selected);
      // === ADDED BY SAMPATH – 2026-01-12 ===
      // Ensure fresh image fetch when opening dialog for an item
      setImageCacheBuster(Date.now());
      setImage(undefined);
      setPreviewUrl("");
      if (inputRef.current) inputRef.current.value = "";
      // === END SAMPATH ===
    }
  }, [selected]);

  useEffect(() => {
    const productId = Number(selected?.productId || 0);
    if (!productId) {
      setToppingPriceRows([]);
      return;
    }

    let cancelled = false;
    api
      .get(`/api/PizzaToppingPrice?productId=${productId}`)
      .then(({ data }) => {
        if (!cancelled) setToppingPriceRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setToppingPriceRows([]);
      });

    return () => {
      cancelled = true;
    };
  }, [selected?.productId]);

  useEffect(() => {
    const productId = Number(selected?.productId || 0);
    if (!productId) {
      setPizzaSizeRows([]);
      return;
    }

    let cancelled = false;
    api
      .get(`/api/PizzaSize/ByProductId?productId=${productId}`)
      .then(({ data }) => {
        if (!cancelled) setPizzaSizeRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setPizzaSizeRows([]);
      });

    return () => {
      cancelled = true;
    };
  }, [selected?.productId]);

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // FIX: stable local preview URL + cleanup (avoid stale previews / memory leaks)
  useEffect(() => {
    if (!image) {
      setPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(image);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);
  // === END SAMPATH ===

  const handleChange = (e: any) => {
    const file = e.target.files ? e.target.files[0] : undefined;
    if (file && file.size > MAX_FILE_SIZE) {
      toast.error("File size must not exceed 2 MB.");
      return;
    }
    setImage(file);
    // === ADDED BY SAMPATH – 2026-01-12 ===
    // If same file selected again, ensure input change can fire by clearing value after set
    if (inputRef.current) inputRef.current.value = "";
    // === END SAMPATH ===
  };

  const handleSubmit = async () => {
    if (!data) return;
    if (image) {
      setLoading(true);
      const formData = new FormData();
      formData.append("formFile", image);

      const response = await api.post(
        `/api/FileUtility/UploadFile?itemid=${data.productId}`,
        formData
      );
      const responseData = response.data?.data[0];

      setImage(undefined);

      if (responseData?.status === "ERROR") {
        toast.error(responseData.errorMessage || "Failed to upload the file.");
        setLoading(false);
        return;
      }

      // === ADDED BY SAMPATH – 2026-01-12 ===
      // FIX: force browser to fetch the new image (avoid old cached image)
      setImageCacheBuster(Date.now());
      // === END SAMPATH ===
    }

    const finalPayload: any = {
      productId: data.productId,
      itemName: data.productName,
      itemDesc: data.productDescription,
      pricedesc: data.pricedesc,
      itemPrice: data.price,
      disableontime: 0,
      disableondate: 0,
      disableonday: 0,
    };

    if (disableValue !== "none") {
      finalPayload[disableValue] = 1;
      if (disableValue === "disableonday") {
        finalPayload.disableday = selectedDays.join("|");
      }
      if (disableValue === "disableontime") {
        if (!data.disablefromtime || !data.disabletotime) {
          toast.error("Please select start and end time");
          return;
        }
        const time = new Date();
        const [f1, f2]: any = data.disablefromtime.split(":");
        time.setHours(f1);
        time.setMinutes(f2);
        finalPayload.disablefromtime = format(time, "MM/dd/yyyy h:mm:ss a");

        const time1 = new Date();
        const [t1, t2]: any = data.disabletotime.split(":");
        time1.setHours(t1);
        time1.setMinutes(t2);
        finalPayload.disabletotime = format(time1, "MM/dd/yyyy h:mm:ss a");
      }
      if (disableValue === "disableondate") {
        if (!data.disablefromdate || !data.disabletodate) {
          toast.error("Please select start and end date");
          return;
        }
        const time = new Date(data.disablefromdate);
        finalPayload.disablefromdate = format(time, "MM/dd/yyyy h:mm:ss a");
        const time1 = new Date(data.disabletodate);
        finalPayload.disabletodate = format(time1, "MM/dd/yyyy h:mm:ss a");
      }
    }

    const response = await api.post(`/api/Product/UpdateItemData`, finalPayload);
    const responseData = response.data?.data?.[0];
    setLoading(false);

    if (responseData?.status === "ERROR") {
      toast.error(responseData.errorMessage || "Failed to upload the file.");
      return;
    }

    toast.success("Product updated successfully");
    onComplete();
  };

  const imageUrl = useMemo(() => {
    if (!data) return noImage;

    // === ADDED BY SAMPATH – 2026-01-12 ===
    // FIX: show local preview immediately, otherwise bust cache for server image
    if (previewUrl) return previewUrl;

    const src = data.imageUrl || "";
    if (!src) return noImage;

    const joiner = src.includes("?") ? "&" : "?";
    return `${src}${joiner}v=${imageCacheBuster}`;
    // === END SAMPATH ===
  }, [previewUrl, data, imageCacheBuster]);

  const isTruthyFlag = (value: any) =>
    value === true || Number(value || 0) === 1 || String(value || "").toLowerCase() === "true";
  const isPizzaProduct = isTruthyFlag((data as any)?.isPizza ?? (data as any)?.IsPizza);
  const pizzaMetaItems = [
    {
      label: "Pizzas In Deal",
      value: String((data as any)?.pizzasInDeal ?? (data as any)?.PizzasInDeal ?? 0),
    },
    {
      label: "Create Your Own",
      value: isTruthyFlag((data as any)?.isCreateYourOwn ?? (data as any)?.IsCreateYourOwn)
        ? "Yes"
        : "No",
    },
    {
      label: "Deal",
      value: isTruthyFlag((data as any)?.isDeal ?? (data as any)?.IsDeal) ? "Yes" : "No",
    },
    {
      label: "Combined Topping",
      value: String(
        Number(
          (data as any)?.totalCombinetoppings ??
            (data as any)?.totalCombineToppings ??
            (data as any)?.CombinedTopping ??
            (data as any)?.combinedTopping ??
            (data as any)?.TotalCombinetoppings ??
            (data as any)?.TotalCombineToppings ??
            0,
        ),
      ),
    },
    {
      label: "IsSub",
      value: isTruthyFlag((data as any)?.isServiceSub ?? (data as any)?.IsServiceSub)
        ? "Yes"
        : "No",
    },
  ];
  const getToppingPriceItems = (row: any) =>
    Array.from({ length: 9 }, (_, count) => {
      const price = Number(row?.[`toppingPrice${count}`] || 0);
      return price > 0 ? { count, price } : null;
    }).filter(Boolean) as { count: number; price: number }[];
  const localToppingPriceRows = [
    {
      sizeNameAbbr: "",
      sizeName: "Topping",
      toppingPrice0: (data as any)?.toppingPrice0,
      toppingPrice1: (data as any)?.toppingPrice1,
      toppingPrice2: (data as any)?.toppingPrice2,
      toppingPrice3: (data as any)?.toppingPrice3,
      toppingPrice4: (data as any)?.toppingPrice4,
      toppingPrice5: (data as any)?.toppingPrice5,
    },
  ].filter((row) => getToppingPriceItems(row).length > 0);
  const displayToppingPriceRows = toppingPriceRows.length
    ? toppingPriceRows
    : localToppingPriceRows;
  const showToppingPriceRows = displayToppingPriceRows.some(
    (row) => getToppingPriceItems(row).length > 0,
  );
  const getSizePriceItems = (row: any) =>
    Array.from({ length: 5 }, (_, index) => {
      const count = index + 1;
      const price = Number(row?.[`sizePriceX${count}`] || 0);
      return price > 0 ? { count, price } : null;
    }).filter(Boolean) as { count: number; price: number }[];
  const showSizePriceRows =
    !showToppingPriceRows &&
    pizzaSizeRows.some((row) => getSizePriceItems(row).length > 0);

  const handelDeleteFile = () => {
    setLoading(true);
    api
      .post(`/api/FileUtility/RemoveUploadFile?itemid=${data!.productId}`)
      .then(() => {
        setImage(undefined);
        setData((e: any) => ({ ...e, imageUrl: "" }));

        // === ADDED BY SAMPATH – 2026-01-12 ===
        // FIX: clear preview + bust cache so old image can’t re-appear
        setPreviewUrl("");
        setImageCacheBuster(Date.now());
        if (inputRef.current) inputRef.current.value = "";
        // === END SAMPATH ===

        onComplete(true);
        toast.success("Image deleted successfully");
      })
      .catch(() => {
        toast.error("Something went wrong while deleting image");
      })
      .finally(() => setLoading(false));
  };

  if (!data) return <></>;

  return (
    <Dialog
      onClose={() => setSelected()}
      maxWidth="xl"
      fullWidth
      open={!!selected}
      PaperProps={{
        sx: {
          width: "min(1180px, calc(100vw - 32px))",
          maxWidth: "1180px",
          borderRadius: "24px",
          overflow: "hidden",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,255,255,0.9))",
          backdropFilter: "blur(14px)",
          boxShadow: "0 26px 70px rgba(15,23,42,0.18)",
        },
      }}
    >
      <div
        className="h-[86vh] max-h-[860px] flex flex-col"
        style={{ fontFamily: '"Noto Sans", sans-serif' }}
      >
        <div className="shrink-0 border-b border-black/5 px-5 py-4 bg-white/92 backdrop-blur-md">
          <div className="mx-auto flex max-w-[1080px] flex-col gap-1 text-center lg:text-left">
            <h4 className="!text-[26px] text-primary font-[900] tracking-[-0.03em]">
              Update Product
            </h4>
            <p className="text-[13px] text-slate-500">
              Refresh product details, image, and availability settings.
            </p>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden bg-[linear-gradient(180deg,rgba(255,255,255,0.5),rgba(255,255,255,0.76))]">
          <div className="grid h-full grid-cols-1 gap-4 overflow-y-auto px-4 py-4 text-[15px] lg:grid-cols-[360px_minmax(0,1fr)] lg:overflow-hidden lg:px-5">
          <div className="flex flex-col gap-3 lg:min-h-0">
            <div className="relative h-[220px] w-full overflow-hidden rounded-[22px] border border-white/70 bg-white shadow-[0_14px_30px_rgba(15,23,42,0.08)] md:h-[280px] lg:sticky lg:top-0 lg:h-[360px]">
            <img
              // === ADDED BY SAMPATH – 2026-01-12 ===
              // FIX: key forces img element to re-mount (prevents stubborn cached render in some browsers)
              key={`${data.productId}-${imageCacheBuster}-${previewUrl ? "preview" : "server"}`}
              // === END SAMPATH ===
              src={imageUrl}
              alt="Product"
              className="w-full h-full object-cover"
              onError={(e: any) => {
                e.target.src = noImage;
              }}
            />
            <input
              id="image-input"
              type="file"
              onChange={handleChange}
              className="hidden"
              // === ADDED BY SAMPATH – 2026-01-12 ===
              ref={inputRef}
              // === END SAMPATH ===
            />
            <div
              className="size-10 flex justify-center items-center absolute bottom-[12px] right-[12px] rounded-full bg-primary shadow-[0_10px_20px_rgba(15,23,42,0.16)] cursor-pointer"
              onClick={() => {
                // === ADDED BY SAMPATH – 2026-01-12 ===
                // FIX: clear input value BEFORE opening picker so selecting same filename still triggers change
                if (inputRef.current) inputRef.current.value = "";
                // === END SAMPATH ===
                const input = document.getElementById("image-input")!;
                input.click();
              }}
            >
              <Edit className="!text-[18px] text-white" />
            </div>
            <div
              className="size-10 flex justify-center items-center absolute bottom-[12px] left-[12px] rounded-full bg-primary shadow-[0_10px_20px_rgba(15,23,42,0.16)] cursor-pointer"
              onClick={handelDeleteFile}
            >
              <DeleteRoundedIcon className="!text-[18px] text-white" />
            </div>
          </div>
            {isPizzaProduct && (
              <div className="rounded-[18px] border border-white/70 bg-white/80 p-3 shadow-[0_14px_30px_rgba(15,23,42,0.06)]">
                <div className="grid grid-cols-2 gap-2">
                  {pizzaMetaItems.map((item) => (
                    <div
                      key={item.label}
                      className="min-h-[58px] rounded-[12px] border border-slate-200 bg-slate-50 px-3 py-2.5"
                    >
                      <div className="text-[11px] font-[700] uppercase leading-4 text-slate-600">
                        {item.label}
                      </div>
                      <div className="mt-1 text-[17px] font-[800] leading-5 text-slate-950">
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="min-h-0 flex flex-col gap-4 lg:overflow-y-auto lg:pr-1">
            <div className="rounded-[22px] border border-white/70 bg-white/80 p-4 shadow-[0_14px_30px_rgba(15,23,42,0.06)]">
              <p className="mb-3 text-[12px] font-[800] uppercase tracking-[0.16em] text-slate-500">
                Basic Info
              </p>
              {isPizzaProduct && (
                <div className="mb-4 rounded-[16px] border border-slate-200 bg-white/95 p-2 shadow-inner">
                  {showToppingPriceRows && (
                    <div className="mt-2 rounded-[12px] border border-emerald-100 bg-emerald-50/60 px-3 py-2">
                      <div className="mb-2 text-[12px] font-[700] uppercase leading-4 text-emerald-800">
                        Topping Price By Size
                      </div>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {displayToppingPriceRows.map((row: any, rowIndex: number) => (
                          getToppingPriceItems(row).length > 0 && (
                          <div
                            key={`${row?.sizeId || rowIndex}-${row?.sizeNameAbbr || "size"}`}
                            className="rounded-[10px] border border-emerald-200 bg-white px-2.5 py-2"
                          >
                            <div className="mb-1 text-[12px] font-bold text-emerald-800">
                              {row?.sizeNameAbbr || row?.sizeName || "Size"}
                              {!!row?.sizeName && row?.sizeName !== row?.sizeNameAbbr
                                ? ` - ${row.sizeName}`
                                : ""}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {getToppingPriceItems(row).map((item) => (
                                <span
                                  key={item.count}
                                  className="inline-flex h-7 items-center gap-1.5 rounded-[7px] bg-emerald-50 px-2 text-[12px]"
                                >
                                  <span className="font-medium text-slate-600">
                                    {item.count === 0 ? "Base" : item.count}
                                  </span>
                                  <span className="font-bold text-slate-950">
                                    ${item.price.toFixed(2)}
                                  </span>
                                </span>
                              ))}
                            </div>
                          </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                  {showSizePriceRows && (
                    <div className="mt-2 rounded-[12px] border border-sky-100 bg-sky-50/60 px-3 py-2">
                      <div className="mb-2 text-[12px] font-[700] uppercase leading-4 text-sky-800">
                        Size Price By Count
                      </div>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {pizzaSizeRows.map((row: any, rowIndex: number) => (
                          getSizePriceItems(row).length > 0 && (
                          <div
                            key={`${row?.sizeId || rowIndex}-${row?.sizeName || "size"}`}
                            className="rounded-[10px] border border-sky-200 bg-white px-2.5 py-2"
                          >
                            <div className="mb-1 text-[12px] font-bold text-sky-800">
                              {row?.sizeName || "Size"}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {getSizePriceItems(row).map((item) => (
                                <span
                                  key={item.count}
                                  className="inline-flex h-7 items-center gap-1.5 rounded-[7px] bg-sky-50 px-2 text-[12px]"
                                >
                                  <span className="font-medium text-slate-600">
                                    {item.count}
                                  </span>
                                  <span className="font-bold text-slate-950">
                                    ${item.price.toFixed(2)}
                                  </span>
                                </span>
                              ))}
                            </div>
                          </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-col gap-3">
                <Input
                  size="small"
                  label="Name"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "rgba(255,255,255,0.88)",
                    },
                  }}
                  value={data.productName}
                  onValueChange={(productName) =>
                    setData((e: any) => ({ ...e, productName }))
                  }
                />
                <Input
                  size="small"
                  label="Price"
                  type="number"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "rgba(255,255,255,0.88)",
                    },
                  }}
                  value={data.price}
                  onValueChange={(price) =>
                    setData((e: any) => ({ ...e, price: +price }))
                  }
                />
                <Input
                  size="small"
                  rows={3}
                  label="Description"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "rgba(255,255,255,0.88)",
                    },
                  }}
                  value={data.productDescription}
                  onValueChange={(productDescription) =>
                    setData((e: any) => ({
                      ...e,
                      productDescription,
                    }))
                  }
                />
                <Input
                  size="small"
                  rows={3}
                  label="Price Description"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      backgroundColor: "rgba(255,255,255,0.88)",
                    },
                  }}
                  value={data.pricedesc}
                  onValueChange={(pricedesc) =>
                    setData((e: any) => ({
                      ...e,
                      pricedesc,
                    }))
                  }
                />
              </div>
            </div>

            <div className="rounded-[22px] border border-white/70 bg-white/80 p-4 shadow-[0_14px_30px_rgba(15,23,42,0.06)]">
              <FormControl className="w-full">
                <p className="mb-3 text-[12px] font-[800] uppercase tracking-[0.16em] text-slate-500">
                  Disable Options
                </p>
                <div className="border rounded-[18px] border-black/10 bg-white/82 overflow-hidden">
                  <div className="border-b border-black/10 px-3 py-2.5">
                    <RadioGroup
                      row
                      value={disableValue}
                      onChange={(e) => setDisableValue(e.target.value)}
                    >
                      <FormControlLabel
                        value="none"
                        control={<Radio size="small" />}
                        label="None"
                      />
                      <FormControlLabel
                        value="disableontime"
                        control={<Radio size="small" />}
                        label="Disable on Time"
                      />
                      <FormControlLabel
                        value="disableondate"
                        control={<Radio size="small" />}
                        label="Disable on Date"
                      />
                      <FormControlLabel
                        value="disableonday"
                        control={<Radio size="small" />}
                        label="Disable on Day"
                      />
                    </RadioGroup>
                  </div>
                  <div className="p-3 flex flex-wrap gap-2">
                    {disableValue === "none" && (
                      <p className="text-center text-slate-600">
                        No disable option for this product
                      </p>
                    )}
                    {disableValue === "disableontime" && (
                      <>
                        <Input
                          size="small"
                          type="time"
                          label="Start Time"
                          className="flex-1"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "14px",
                              backgroundColor: "rgba(255,255,255,0.88)",
                            },
                          }}
                          value={data.disablefromtime}
                          onValueChange={(disablefromtime) =>
                            setData((e: any) => ({ ...e, disablefromtime }))
                          }
                        />
                        <Input
                          size="small"
                          type="time"
                          label="End Time"
                          className="flex-1"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "14px",
                              backgroundColor: "rgba(255,255,255,0.88)",
                            },
                          }}
                          value={data.disabletotime}
                          onValueChange={(disabletotime) =>
                            setData((e: any) => ({ ...e, disabletotime }))
                          }
                        />
                      </>
                    )}
                    {disableValue === "disableondate" && (
                      <>
                        <Input
                          size="small"
                          type="date"
                          label="Start Date"
                          className="flex-1"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "14px",
                              backgroundColor: "rgba(255,255,255,0.88)",
                            },
                          }}
                          value={data.disablefromdate}
                          onValueChange={(disablefromdate) =>
                            setData((e: any) => ({ ...e, disablefromdate }))
                          }
                        />
                        <Input
                          size="small"
                          type="date"
                          label="End Date"
                          className="flex-1"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "14px",
                              backgroundColor: "rgba(255,255,255,0.88)",
                            },
                          }}
                          value={data.disabletodate}
                          onValueChange={(disabletodate) =>
                            setData((e: any) => ({ ...e, disabletodate }))
                          }
                        />
                      </>
                    )}
                    {disableValue === "disableonday" && (
                      <FormGroup row className="flex-wrap">
                        {days.map((day) => (
                          <FormControlLabel
                            key={day.value}
                            control={
                              <Checkbox
                                checked={selectedDays.includes(day.value)}
                                onChange={() => handleCheckboxChange(day.value)}
                              />
                            }
                            label={day.label}
                          />
                        ))}
                      </FormGroup>
                    )}
                  </div>
                </div>
              </FormControl>
            </div>
          </div>
        </div>
        </div>
        <div className="sticky bottom-0 z-20 px-4 py-3 border-t border-black/5 bg-white/94 backdrop-blur-md">
          <Button
            className="!w-full !rounded-[16px] !py-3.5 !font-[800]"
            disabled={loading}
            onClick={handleSubmit}
          >
            Update
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default EditProductDialog;
